import { useEffect, useState } from "react";
import { UserAiJobItemResponseStatus } from "@apis/generated/openapi";
import { useAppDispatch } from "@hooks/redux";
import { fetchAiCredits } from "@store/globalSlice";
import { createVideoJobStatusPoll$, type VideoJobLiveStatus } from "./videoJobStatusPoll";

export type { VideoJobLiveStatus };

/**
 * 单卡片自轮询任务状态：当卡片自身处于进行中（processing/queued）时，订阅 RxJS 串行轮询
 * （见 videoJobStatusPoll.ts），据此自更新状态、封面与视频地址。命中终态或超时后流自动完成。
 * 与列表/侧边栏完全解耦：不刷新列表，仅在自身失败时刷新积分余额（失败会退还积分）。
 */
export const useVideoJobStatusPoll = (
  jobId: number | string | undefined,
  enabled: boolean,
): VideoJobLiveStatus | null => {
  const dispatch = useAppDispatch();
  const [live, setLive] = useState<VideoJobLiveStatus | null>(null);

  useEffect(() => {
    if (!enabled || !jobId) {
      setLive(null);
      return;
    }

    let failureNotified = false;
    const sub = createVideoJobStatusPoll$(jobId).subscribe({
      next: (s) => {
        setLive(s);
        if (s.status === UserAiJobItemResponseStatus.failed && !failureNotified) {
          failureNotified = true;
          dispatch(fetchAiCredits());
        }
      },
      // 经 retry 仍失败则静默：保留卡片当前展示，等待下一次进入或手动刷新
      error: () => {},
    });

    return () => sub.unsubscribe();
  }, [enabled, jobId, dispatch]);

  return live;
};
